export default function EdenPrairieTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Eden Prairie Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Eden Prairie travelers actually move helps us anticipate the passport urgencies that arise here. From medical device regulatory sprints to family river cruise coordination, southwest metro travel operates on a unique rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* MSP Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSP — Minneapolis-Saint Paul International Airport</h3>
                <p className="text-gray-400 text-xs">About 15 miles — 20 minutes via I-494</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              MSP Airport is highly accessible from Eden Prairie via I-494. With nonstop service to Amsterdam, London, Paris, Tokyo, Frankfurt, and Reykjavik, many residents connect directly to Europe and Asia. The airport&apos;s accessibility from the southwest metro makes it a convenient hub for both corporate and leisure travel. When a passport issue surfaces with an MSP departure booked, the urgency is immediate.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking MSP international connections. Most countries require six months validity beyond your return date.
              </p>
            </div>
          </div>

          {/* Medical Device & Technology Corporate Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-stethoscope-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Medical Device &amp; Technology Corporate Travel</h3>
                <p className="text-gray-400 text-xs">MTS Systems, Starkey, and Eden Prairie&apos;s industry-leading employers</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Eden Prairie&apos;s medical device and technology employers — MTS Systems, Starkey Hearing Technologies, and dozens of software and financial services companies — maintain active international operations. Their employees travel to Germany, Japan, Singapore, and the UK for regulatory meetings, product launches, and client engagements, often on 10–21 day notice. Regulatory meeting schedules do not accommodate passport delays.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Medical device professionals traveling internationally should maintain a passport with at least 12 months remaining validity. Regulatory meetings are fixed and non-negotiable.
              </p>
            </div>
          </div>

          {/* Family Leisure Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Family Leisure Travel</h3>
                <p className="text-gray-400 text-xs">European river cruises, Caribbean packages, and school trips abroad</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Eden Prairie&apos;s high-quality schools and family-oriented culture generate significant leisure travel. Families book European river cruises, Mediterranean packages, and Caribbean vacations. The challenge: children&apos;s passports expire after 5 years, and parents often realize expiration issues only after non-refundable travel is booked. A family near Purgatory Creek plans a cruise and realizes their daughter&apos;s passport expired last year.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Start a family passport tracking spreadsheet. Include every family member&apos;s expiration date. Review it each September before spring break booking begins.
              </p>
            </div>
          </div>

          {/* Eden Prairie High School International Programs */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Eden Prairie High School International Programs</h3>
                <p className="text-gray-400 text-xs">Model UN, study abroad, and international exchange students</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Eden Prairie High School is known for its academic excellence and international programming — Model UN, exchange programs, and educational trips abroad. Many students realize they need a first-time passport or renewal only after being accepted to these programs, with departure dates that leave little margin. The school&apos;s world-class athletics programs also generate travel for international competitions and tournaments.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> High school students should apply for a passport in 10th grade, before needing it for travel. First-time applicants require an in-person acceptance clerk visit — start early.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Flying Cloud Airport &amp; Private Aviation</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Eden Prairie is home to Flying Cloud Airport, a general aviation hub for private and corporate charter flights. Eden Prairie&apos;s corporate community includes executives who travel via private charter to international hubs. Even private aviation passengers require valid passports for international travel. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}